/**
 * `/liturgies/new` — the four-step creation wizard.
 *
 * Designed from the mockup's `isCreate` block (880px max width):
 *   Step 1: Celebration context (type, rite, jurisdiction, interface language)
 *   Step 2: Template selection & title (pins immutable version)
 *   Step 3: Wall-clock date, time, timezone, place & liturgical day context
 *   Step 4: Collaborators & capability access summary
 *
 * Choosing a Template Version here **pins it**, permanently. Everything the workspace will contain
 * is decided at this moment, and publishing a newer version later will not change it
 * (docs/engineering/01-domain-model.md).
 *
 * The celebration time is **local wall-clock time plus a zone**, never an instant: a 10:00 Mass is
 * at 10:00 in the parish's own zone regardless of where the server runs.
 */

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { fetchTemplates } from '../templates/api';
import { ApiError, createLiturgy } from './api';
import { Notice, TimezoneSelect } from '../../components/primitives';
import './liturgies.css';

const STEP_TITLES = [
  'Begin a celebration',
  'Choose a template',
  'Date and place of celebration',
  'Collaborators & access',
];

const STEP_BLURBS = [
  'Set the liturgical rite and context. These decide which templates, customary defaults, and options are offered.',
  'A template decides which sections, items, and roles this liturgy will contain. The version you choose is pinned permanently to this celebration.',
  "Wall-clock celebration time and location. The time stays as written in the parish's own zone regardless of where the viewer is located.",
  'Creation establishes you as Owner. You may invite collaborators now or from the Access screen later — Capability Links can be revoked and regenerated at any time.',
];

const STEP_HINTS = [
  'Step 1 of 4 · Context determines template options',
  'Step 2 of 4 · Title and template version required',
  'Step 3 of 4 · Wall-clock schedule and venue',
  'Step 4 of 4 · Review and establish ownership',
];

export function CreateLiturgyScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1 state
  const [celebrationType, setCelebrationType] = useState('Marriage — Wedding Mass');
  const [rite, setRite] = useState('Roman Rite');
  const [jurisdiction, setJurisdiction] = useState('Archdiocese of Atlanta');
  const [interfaceLanguage, setInterfaceLanguage] = useState('English');

  // Step 2 state
  const [title, setTitle] = useState('');
  const [versionId, setVersionId] = useState('');

  // Step 3 state
  const [date, setDate] = useState('');
  const [time, setTime] = useState('11:00');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [place, setPlace] = useState('');

  // Step 4 state
  const [participantNote, setParticipantNote] = useState('');
  const [cantorNote, setCantorNote] = useState('');

  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });

  // Only published versions are selectable.
  const available = (templates.data ?? []).filter(
    (t) => t.latest_published_version_id !== null && t.retired_at === null,
  );

  const localDateTime = date ? `${date}T${time || '00:00'}:00.000Z` : null;

  const create = useMutation({
    mutationFn: () =>
      createLiturgy({
        title: title.trim(),
        template_version_id: versionId,
        celebration_local_datetime: localDateTime,
        celebration_timezone: date ? timezone : null,
        place: place.trim() || null,
      }),
    onSuccess: async (created) => {
      if (created.administrator_link) {
        await navigate({
          to: '/liturgies/$id/created',
          params: { id: created.id },
          state: { administratorLink: created.administrator_link } as never,
        });
        return;
      }

      await navigate({ to: '/liturgies/$id', params: { id: created.id } });
    },
  });

  const error = create.error instanceof ApiError ? create.error : undefined;

  const handleNext = (e: FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (!title.trim() || !versionId) return;
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      create.mutate();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => (s - 1) as 1 | 2 | 3 | 4);
    }
  };

  const selectedTemplate = available.find((t) => t.latest_published_version_id === versionId);

  return (
    <div className="lit-create">
      <div className="lit-create__head">
        <p className="lit-eyebrow">New liturgy · step {step} of 4</p>
        <div className="lit-create__divider" />
        <span className="lit-create__route">/liturgies/new</span>
      </div>

      <h1 className="lit-create__title">{STEP_TITLES[step - 1]}</h1>
      <p className="lit-create__lede">{STEP_BLURBS[step - 1]}</p>

      <div className="lit-create__bars" aria-hidden="true">
        {[1, 2, 3, 4].map((barStep) => (
          <div
            key={barStep}
            className={`lit-create__bar ${
              barStep < step
                ? 'lit-create__bar--complete'
                : barStep === step
                  ? 'lit-create__bar--active'
                  : ''
            }`}
          />
        ))}
      </div>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <form onSubmit={handleNext} noValidate className="lit-create__form">
        {step === 1 ? (
          <div className="lit-create__row">
            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.celebration_type')}</span>
              <select value={celebrationType} onChange={(e) => setCelebrationType(e.target.value)}>
                <option>{t('liturgies.marriage_mass')}</option>
                <option>{t('liturgies.marriage_ceremony')}</option>
                <option>{t('liturgies.funeral_mass')}</option>
                <option>{t('liturgies.baptism')}</option>
                <option>{t('liturgies.confirmation')}</option>
                <option>{t('liturgies.sunday_mass')}</option>
                <option>{t('liturgies.quinceanera_mass')}</option>
              </select>
            </label>

            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.rite')}</span>
              <select value={rite} onChange={(e) => setRite(e.target.value)}>
                <option>{t('liturgies.roman_rite')}</option>
                <option>{t('liturgies.ambrosian_rite')}</option>
                <option>{t('liturgies.byzantine_rite')}</option>
                <option>{t('liturgies.maronite_rite')}</option>
                <option>{t('liturgies.chaldean_rite')}</option>
              </select>
            </label>

            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.jurisdiction')}</span>
              <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}>
                <option>{t('liturgies.archdiocese_atlanta')}</option>
                <option>{t('liturgies.diocese_savannah')}</option>
                <option>{t('liturgies.other_jurisdiction')}</option>
              </select>
              <span className="lit-field__hint">
                Defaulted from your Account. Overridable per Liturgy.
              </span>
            </label>

            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.interface_language')}</span>
              <select
                value={interfaceLanguage}
                onChange={(e) => setInterfaceLanguage(e.target.value)}
              >
                <option>English</option>
                <option>Español</option>
              </select>
              <span className="lit-field__hint">{t('liturgies.export_lang_hint')}</span>
            </label>
          </div>
        ) : null}

        {step === 2 ? (
          <div>
            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.liturgy_title')}</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Miller — Davis Wedding"
                required
                autoFocus
              />
              <span className="lit-field__hint">
                Names this celebration in your dashboard, planning views, and exports.
              </span>
            </label>

            <p className="lit-field__label" style={{ marginTop: 20 }}>
              Available templates
            </p>
            <div className="lit-create__template-list">
              {available.map((tpl) => {
                const isSelected = versionId === tpl.latest_published_version_id;
                return (
                  <div
                    key={tpl.id}
                    className={`lit-create__template-card ${
                      isSelected ? 'lit-create__template-card--selected' : ''
                    }`}
                    onClick={() => setVersionId(tpl.latest_published_version_id ?? '')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setVersionId(tpl.latest_published_version_id ?? '');
                      }
                    }}
                  >
                    <div
                      className={`lit-create__dot ${isSelected ? 'lit-create__dot--selected' : ''}`}
                    />
                    <div className="lit-create__template-info">
                      <div className="lit-create__template-name">{tpl.name}</div>
                      <div className="lit-create__template-meta">
                        {tpl.owner_display
                          ? t('liturgies.by_owner', { owner: tpl.owner_display })
                          : t('liturgies.standard_template')}{' '}
                        · v{tpl.latest_published_version}
                      </div>
                    </div>
                    <span className="lit-create__template-badge">
                      v{tpl.latest_published_version} {t('liturgies.published')}
                    </span>
                  </div>
                );
              })}
              {templates.isSuccess && available.length === 0 ? (
                <Notice>{t('liturgies.no_published_templates')}</Notice>
              ) : null}
            </div>

            <p className="lit-field__hint" style={{ marginTop: 12 }}>
              {t('liturgies.template_version_hint')}
            </p>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="lit-create__row">
            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.date_celebration')}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>

            <label className="lit-field">
              <span className="lit-field__label">{t('liturgies.time')}</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>

            <label className="lit-field" style={{ gridColumn: 'span 2' }}>
              <span className="lit-field__label">{t('liturgies.place_celebration')}</span>
              <input
                type="text"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="St Pius X Church, Conyers"
              />
            </label>

            <label className="lit-field" style={{ gridColumn: 'span 2' }}>
              <span className="lit-field__label">{t('liturgies.timezone')}</span>
              <TimezoneSelect value={timezone} onChange={setTimezone} />
              <span className="lit-field__hint">
                The parish&rsquo;s own zone. The time stays as written regardless of viewer
                location.
              </span>
            </label>

            <div className="lit-create__callout" style={{ gridColumn: 'span 2' }}>
              <div>
                <strong>{t('liturgies.liturgical_day_label')}</strong>{' '}
                {t('liturgies.liturgical_day_hint')}
              </div>
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: '13px', lineHeight: 1.7, color: '#3a3a3a', margin: 0 }}>
              Creation establishes you as <strong>Owner</strong>. You will be able to generate
              anonymous Participant Links and delegate Administrator access immediately.
            </p>

            <div className="lit-create__collaborator-row">
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>Participant Link</div>
                <div style={{ fontSize: '11px', color: '#8a8a8a' }}>Couple / Family Link</div>
              </div>
              <input
                type="text"
                placeholder="Couple / Family name or note"
                value={participantNote}
                onChange={(e) => setParticipantNote(e.target.value)}
              />
              <div style={{ fontSize: '11.5px', color: '#7a7a7a', lineHeight: 1.5 }}>
                Anonymous link generated on creation.
              </div>
            </div>

            <div className="lit-create__collaborator-row">
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>Music Director / Cantor</div>
                <div style={{ fontSize: '11px', color: '#8a8a8a' }}>Delegated section access</div>
              </div>
              <input
                type="text"
                placeholder="Name or email address"
                value={cantorNote}
                onChange={(e) => setCantorNote(e.target.value)}
              />
              <div style={{ fontSize: '11.5px', color: '#7a7a7a', lineHeight: 1.5 }}>
                Can be assigned from the Access screen later.
              </div>
            </div>

            <div
              style={{
                background: '#faf8f3',
                border: '1px solid rgba(212,175,55,0.5)',
                padding: '14px 16px',
                fontSize: '12.5px',
                lineHeight: 1.6,
                color: '#5a5340',
              }}
            >
              <strong>Summary:</strong> Liturgy &ldquo;{title || 'Untitled'}&rdquo; will be created
              using template <em>{selectedTemplate?.name || 'Selected template'}</em>.
            </div>
          </div>
        ) : null}

        <div className="lit-create__acts">
          {step > 1 ? (
            <button type="button" className="lit-btn" onClick={handleBack}>
              Back
            </button>
          ) : (
            <Link className="lit-btn" to="/liturgies">
              Cancel
            </Link>
          )}

          <span className="lit-create__hint">{STEP_HINTS[step - 1]}</span>

          {step < 4 ? (
            <button
              type="submit"
              className="lit-btn lit-btn--primary"
              disabled={step === 2 && (!title.trim() || !versionId)}
            >
              Next step →
            </button>
          ) : (
            <button
              type="submit"
              className="lit-btn lit-btn--primary"
              disabled={create.isPending || !title.trim() || !versionId}
            >
              {create.isPending ? 'Creating…' : 'Create liturgy'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
